import React, { useState, useEffect } from "react";
import {
  List,
  useTable,
  ShowButton,
} from "@refinedev/antd";
import type { BaseRecord } from "@refinedev/core";
import {
  Table,
  Space,
  Tag,
  Typography,
  Button,
  Input,
  Segmented,
  Modal,
  Form,
  InputNumber,
  Select,
  message,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  DownloadOutlined,
  StopOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import type { SegmentedValue } from "antd/es/segmented";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { supabaseClient } from "../../utility/supabaseClient";

const STATUS_SEGMENTS = [
  { label: "全部状态", value: "all" },
  { label: "可用", value: "active" },
  { label: "已用完", value: "used" },
  { label: "已禁用", value: "disabled" },
  { label: "已过期", value: "expired" },
];

const STATUS_META: Record<string, { label: string; color: string }> = {
  active: { label: "可用", color: "green" },
  used: { label: "已用完", color: "default" },
  disabled: { label: "已禁用", color: "red" },
  expired: { label: "已过期", color: "orange" },
};

interface BatchGenerateFormValues {
  tier_id: string;
  count: number;
  max_uses: number;
  valid_days?: number;
  notes?: string;
  batch_id?: string;
}

type CopyFormat = "cardPassword" | "codeOnly";

const formatCodesForOutput = (codes: string[], format: CopyFormat) => {
  if (!codes?.length) return "";
  return format === "cardPassword"
    ? codes.map((code) => `${code}:${code}`).join("\n")
    : codes.join("\n");
};

export const RedemptionCodeList: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const statusFromURL = params.get("status") ?? "all";
  const searchFromURL = params.get("search") ?? "";

  const [searchValue, setSearchValue] = useState(searchFromURL);
  const [statusValue, setStatusValue] = useState(statusFromURL);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);
  const [tiers, setTiers] = useState<any[]>([]);
  const [tiersLoading, setTiersLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportBatchId, setExportBatchId] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [copyFormat, setCopyFormat] = useState<CopyFormat>("cardPassword");
  const [form] = Form.useForm();

  // 加载订阅等级列表
  // 仅在modal打开且tiers未加载时执行，不需要tiers.length作为依赖
  useEffect(() => {
    if (batchModalVisible && tiers.length === 0) {
      loadTiers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchModalVisible]);

  const loadTiers = async () => {
    setTiersLoading(true);
    try {
      const { data, error } = await supabaseClient
        .from("membership_tiers")
        .select("id, name, display_name")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) {
        message.error(`加载等级失败: ${error.message}`);
        return;
      }

      setTiers(data || []);
    } catch (err: any) {
      message.error(`加载等级异常: ${err.message}`);
    } finally {
      setTiersLoading(false);
    }
  };

  const { tableProps, setFilters, searchFormProps, tableQueryResult } = useTable({
    syncWithLocation: true,
    sorters: {
      initial: [{ field: "created_at", order: "desc" }],
    },
    meta: {
      select: "*, membership_tiers(name, display_name)",
    },
  });
  const handleStatusChange = (value: SegmentedValue) => {
    const nextValue = String(value);
    setStatusValue(nextValue);

    const filters: any[] = [];
    if (nextValue !== "all") {
      if (nextValue === "expired") {
        filters.push({
          field: "valid_until",
          operator: "lt",
          value: new Date().toISOString(),
        });
      } else {
        filters.push({
          field: "status",
          operator: "eq",
          value: nextValue,
        });
      }
    }
    if (searchValue) {
      filters.push({
        field: "code",
        operator: "contains",
        value: searchValue,
      });
    }
    setFilters(filters, "replace");
  };

  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    setSearchValue(trimmed);

    const filters: any[] = [];
    if (trimmed) {
      filters.push({
        field: "code",
        operator: "contains",
        value: trimmed,
      });
    }
    if (statusValue !== "all") {
      if (statusValue === "expired") {
        filters.push({
          field: "valid_until",
          operator: "lt",
          value: new Date().toISOString(),
        });
      } else {
        filters.push({
          field: "status",
          operator: "eq",
          value: statusValue,
        });
      }
    }
    setFilters(filters, "replace");
  };

  // 批量生成兑换码
  const handleBatchGenerate = async (values: BatchGenerateFormValues) => {
    setGenerating(true);
    try {
      const valid_until = values.valid_days
        ? dayjs().add(values.valid_days, "day").toISOString()
        : null;

      const { data, error } = await supabaseClient.rpc("batch_generate_codes", {
        p_tier_id: values.tier_id,
        p_count: values.count,
        p_max_uses: values.max_uses,
        p_valid_until: valid_until,
        p_notes: values.notes || null,
        p_batch_id: values.batch_id || null,
      });

      if (error) {
        message.error(`生成失败: ${error.message}`);
        return;
      }

      const codes = data.map((row: any) => row.code);
      setGeneratedCodes(codes);
      message.success(`成功生成 ${codes.length} 个兑换码！`);

      // 刷新列表但保持当前弹窗，方便复制
      tableQueryResult?.refetch?.();
    } catch (err: any) {
      message.error(`生成异常: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  // 导出兑换码为文本
  const downloadCodesAsTxt = (codes: string[], format: CopyFormat, label = "redemption_codes") => {
    const text = formatCodesForOutput(codes, format);
    if (!text) {
      message.warning("暂无可导出的兑换码");
      return;
    }
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${label}_${dayjs().format("YYYYMMDD_HHmmss")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // 复制兑换码
  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    message.success("已复制到剪贴板");
  };

  // 复制已勾选的兑换码
  const copySelectedCodes = (format: CopyFormat) => {
    if (!selectedCodes.length) {
      message.warning("请先勾选要复制的兑换码");
      return;
    }
    const text = formatCodesForOutput(selectedCodes, format);
    const label = format === "cardPassword" ? "卡号:密码" : "兑换码";
    navigator.clipboard.writeText(text);
    message.success(`已按${label}格式复制 ${selectedCodes.length} 个兑换码`);
  };

  // 批量复制为平台要求格式
  const copyFormattedCodes = (format: CopyFormat) => {
    const text = formatCodesForOutput(generatedCodes, format);
    if (!text) {
      message.warning("暂无可复制的兑换码");
      return;
    }
    const label = format === "cardPassword" ? "卡号:密码" : "兑换码";
    navigator.clipboard.writeText(text);
    message.success(`已按${label}格式复制`);
  };

  // 按批次导出TXT
  const exportBatchCodes = async () => {
    const batchId = exportBatchId.trim();
    if (!batchId) {
      message.warning("请输入批次ID");
      return;
    }
    setExportLoading(true);
    try {
      const { data, error } = await supabaseClient
        .from("redemption_codes")
        .select("code")
        .eq("batch_id", batchId)
        .order("created_at", { ascending: true })
        .range(0, 4999); // 安全上限覆盖常见批量导出

      if (error) {
        message.error(`导出失败: ${error.message}`);
        return;
      }

      const codes = (data || [])
        .map((row: any) => row.code)
        .filter((code: string | undefined): code is string => Boolean(code));

      if (!codes.length) {
        message.info("未找到该批次的兑换码");
        return;
      }

      downloadCodesAsTxt(codes, copyFormat, `redemption_codes_${batchId}`);
      message.success(`已导出批次 ${batchId} 的兑换码，共 ${codes.length} 条`);
      setExportModalVisible(false);
    } catch (err: any) {
      message.error(`导出异常: ${err.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  // 禁用兑换码（类型守卫：确保 id 是有效的 UUID 字符串）
  const disableCode = async (id: BaseRecord["id"]) => {
    // 类型守卫：验证 id 存在且为字符串（Supabase UUID 必须是 string）
    if (!id || typeof id !== "string") {
      message.error("无效的兑换码ID格式");
      return;
    }

    // 此时 TypeScript 知道 id 必定是 string
    try {
      const { error } = await supabaseClient
        .from("redemption_codes")
        .update({
          status: "disabled",
          disabled_at: new Date().toISOString(),
        })
        .eq("id", id);  // ✅ TypeScript: id is string

      if (error) {
        message.error(`禁用失败: ${error.message}`);
        return;
      }

      message.success("已禁用该兑换码");
      tableQueryResult?.refetch?.();
    } catch (err: any) {
      message.error(`禁用异常: ${err.message}`);
    }
  };

  return (
    <>
      <Form form={searchFormProps?.form} style={{ display: "none" }} />

      <List
        title="兑换码管理"
        headerButtons={({ defaultButtons }) => (
          <Space size="middle" wrap>
            <Space.Compact style={{ width: 280 }}>
              <Input
                allowClear
                placeholder="输入兑换码搜索"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onPressEnter={() => handleSearch(searchValue)}
              />
              <Button type="primary" onClick={() => handleSearch(searchValue)}>
                搜索
              </Button>
            </Space.Compact>
            <Segmented
              options={STATUS_SEGMENTS}
              value={statusValue}
              onChange={handleStatusChange}
            />
            <Segmented
              options={[
                { label: "卡号:密码", value: "cardPassword" },
                { label: "仅兑换码", value: "codeOnly" },
              ]}
              value={copyFormat}
              onChange={(value) => setCopyFormat(value as CopyFormat)}
            />
            <Tooltip title="勾选列表后，可一键按所选格式复制；仅有兑换码时，卡号与密码将相同">
              <Button
                icon={<CopyOutlined />}
                disabled={!selectedCodes.length}
                onClick={() => copySelectedCodes(copyFormat)}
              >
                批量复制
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setBatchModalVisible(true)}
            >
              批量生成
            </Button>
            <Button onClick={() => setExportModalVisible(true)}>
              按批次导出TXT
            </Button>
          </Space>
        )}
      >
        <Table
          {...tableProps}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: (keys, rows) => {
              setSelectedRowKeys(keys);
              setSelectedCodes(
                rows
                  .map((row: any) => row?.code)
                  .filter((code: string | undefined): code is string => Boolean(code))
              );
            },
          }}
        >
          <Table.Column
            dataIndex="code"
            title="兑换码"
            render={(value: string) => (
              <Space>
                <Typography.Text
                  copyable={{
                    text: value,
                    tooltips: ["点击复制", "已复制"],
                  }}
                  code
                  style={{ fontSize: 14 }}
                >
                  {value}
                </Typography.Text>
              </Space>
            )}
          />
          <Table.Column
            dataIndex="membership_tiers"
            title="等级"
            render={(tier: any) => (
              <Tag color="blue">{tier?.display_name || "未知"}</Tag>
            )}
          />
          <Table.Column
            dataIndex="status"
            title="状态"
            render={(value: string, record: any) => {
              const now = new Date();
              const isExpired = record.valid_until && new Date(record.valid_until) < now;
              const isUsedOut = value === "active" && record.current_uses >= record.max_uses;
              const normalized = isExpired ? "expired" : isUsedOut ? "used" : value;
              const status = STATUS_META[normalized];
              return <Tag color={status?.color ?? "default"}>{status?.label ?? normalized}</Tag>;
            }}
          />
          <Table.Column
            dataIndex="current_uses"
            title="使用情况"
            render={(current: number, record: any) => (
              <Typography.Text>
                {current} / {record.max_uses}
              </Typography.Text>
            )}
          />
          <Table.Column
            dataIndex="valid_until"
            title="有效期"
            render={(value?: string) =>
              value ? (
                <Tooltip title={dayjs(value).format("YYYY-MM-DD HH:mm:ss")}>
                  {dayjs(value).format("YYYY-MM-DD")}
                </Tooltip>
              ) : (
                <Typography.Text type="secondary">永久</Typography.Text>
              )
            }
          />
          <Table.Column
            dataIndex="batch_id"
            title="批次"
            render={(value?: string) => (
              <Typography.Text ellipsis style={{ maxWidth: 120 }}>
                {value || "-"}
              </Typography.Text>
            )}
          />
          <Table.Column
            dataIndex="notes"
            title="备注"
            render={(value?: string) => (
              <Typography.Text ellipsis style={{ maxWidth: 150 }}>
                {value || "-"}
              </Typography.Text>
            )}
          />
          <Table.Column
            dataIndex="created_at"
            title="创建时间"
            render={(value: string) => dayjs(value).format("YYYY-MM-DD HH:mm")}
          />
          <Table.Column
            title="操作"
            dataIndex="actions"
            render={(_, record: BaseRecord) => (
              <Space>
                <Tooltip title="复制兑换码">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyCode(record.code)}
                  />
                </Tooltip>
                <ShowButton hideText size="small" recordItemId={record.id} />
                {record.status === "active" && (
                  <Popconfirm
                    title="确定禁用此兑换码？"
                    onConfirm={() => disableCode(record.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Tooltip title="禁用">
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<StopOutlined />}
                      />
                    </Tooltip>
                  </Popconfirm>
                )}
              </Space>
            )}
          />
        </Table>
      </List>

      {/* 批量生成对话框 */}
      <Modal
        title="批量生成兑换码"
        open={batchModalVisible}
        onCancel={() => {
          setBatchModalVisible(false);
          setGeneratedCodes([]);
          setCopyFormat("cardPassword");
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        {generatedCodes.length === 0 ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleBatchGenerate}
            initialValues={{
              count: 10,
              max_uses: 1,
              valid_days: 30,
            }}
          >
            <Form.Item
              label="订阅等级"
              name="tier_id"
              rules={[{ required: true, message: "请选择订阅等级" }]}
            >
              <Select
                placeholder="选择要生成的会员等级"
                loading={tiersLoading}
                options={tiers.map((tier) => ({
                  label: `${tier.display_name} (${tier.name})`,
                  value: tier.id,
                }))}
              />
            </Form.Item>

            <Form.Item
              label="生成数量"
              name="count"
              rules={[
                { required: true, message: "请输入生成数量" },
                {
                  type: "number",
                  min: 1,
                  max: 1000,
                  message: "数量范围：1-1000",
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="一次最多生成1000个"
                min={1}
                max={1000}
              />
            </Form.Item>

            <Form.Item
              label="单码可用次数"
              name="max_uses"
              rules={[{ required: true, message: "请输入可用次数" }]}
              tooltip="每个兑换码可以被使用的次数（1=一次性码，>1=多次码）"
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="通常设为1（一次性码）"
                min={1}
                max={100}
              />
            </Form.Item>

            <Form.Item
              label="有效天数"
              name="valid_days"
              tooltip="兑换码的有效期（从现在开始计算），留空表示永久有效"
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="例如：30（30天后过期），留空=永久"
                min={1}
                max={3650}
              />
            </Form.Item>

            <Form.Item label="批次ID" name="batch_id" tooltip="用于标识这批兑换码，留空则自动生成">
              <Input placeholder="例如：2025双十一活动（可选）" />
            </Form.Item>

            <Form.Item label="备注" name="notes">
              <Input.TextArea
                rows={2}
                placeholder="例如：闲鱼销售、合作伙伴赠送等"
              />
            </Form.Item>

            <Form.Item>
              <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                <Button onClick={() => setBatchModalVisible(false)}>
                  取消
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={generating}
                  icon={<PlusOutlined />}
                >
                  立即生成
                </Button>
              </Space>
            </Form.Item>
          </Form>
        ) : (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Title level={5}>
              <CheckCircleOutlined style={{ color: "#52c41a" }} /> 成功生成{" "}
              {generatedCodes.length} 个兑换码
            </Typography.Title>
            <Typography.Paragraph type="secondary">
              请及时保存这些兑换码，关闭对话框后将无法再次查看。
            </Typography.Paragraph>
            <Space align="center" size="small">
              <Typography.Text strong>批量复制格式</Typography.Text>
              <Segmented
                value={copyFormat}
                onChange={(value) => setCopyFormat(value as CopyFormat)}
                options={[
                  { label: "卡号:密码", value: "cardPassword" },
                  { label: "仅兑换码", value: "codeOnly" },
                ]}
              />
              <Tooltip title="目标平台支持“卡号:密码”或单独的兑换码；当前仅有兑换码，因此卡号与密码会使用同一串码，便于批量粘贴。">
                <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
              </Tooltip>
            </Space>
            <Input.TextArea
              value={formatCodesForOutput(generatedCodes, copyFormat)}
              rows={10}
              readOnly
              style={{ fontFamily: "monospace", fontSize: 12 }}
            />
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <Button
                onClick={() => {
                  setBatchModalVisible(false);
                  setGeneratedCodes([]);
                  form.resetFields();
                }}
              >
                关闭
              </Button>
              <Space>
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => copyFormattedCodes(copyFormat)}
                >
                  复制当前格式
                </Button>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadCodesAsTxt(generatedCodes, copyFormat)}
                >
                  导出TXT文件
                </Button>
              </Space>
            </Space>
            <Typography.Text type="secondary">
              支持格式：卡号:密码 或 单独的兑换码（每行一个）；当前卡号与密码相同，满足需要卡密字段的平台。
            </Typography.Text>
          </Space>
        )}
      </Modal>

      {/* 按批次导出TXT */}
      <Modal
        title="按批次导出兑换码"
        open={exportModalVisible}
        okText="下载TXT"
        cancelText="取消"
        confirmLoading={exportLoading}
        onOk={exportBatchCodes}
        onCancel={() => {
          setExportModalVisible(false);
          setExportBatchId("");
        }}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Typography.Paragraph type="secondary">
            适用于批量生成 1000 条以上无法一次勾选的场景。输入批次ID后，将按所选格式导出整批兑换码。
          </Typography.Paragraph>
          <Input
            placeholder="请输入批次ID（与生成时填写的批次ID一致）"
            value={exportBatchId}
            onChange={(e) => setExportBatchId(e.target.value)}
          />
          <Space align="center" size="small">
            <Typography.Text strong>导出格式</Typography.Text>
            <Segmented
              options={[
                { label: "卡号:密码", value: "cardPassword" },
                { label: "仅兑换码", value: "codeOnly" },
              ]}
              value={copyFormat}
              onChange={(value) => setCopyFormat(value as CopyFormat)}
            />
            <Tooltip title="仅有兑换码时，会生成 兑换码:兑换码 以兼容卡密平台；若目标平台只需单码，可选择“仅兑换码”。">
              <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
            </Tooltip>
          </Space>
        </Space>
      </Modal>
    </>
  );
};
